import { Outlet, createRoute, redirect } from '@tanstack/react-router';
import { rootRoute } from './root.js';
import { isAuthenticated } from '../auth/AuthContext.js';
import { getAccessToken } from '../auth/tokenStore.js';
import { decodeJwtRoleClaim } from '../auth/jwtClaims.js';
import { EmployeeLayout } from '../components/EmployeeLayout.js';

/**
 * /me/* layout — the employee self-service portal.
 *
 * beforeLoad gate handles two cases:
 *   1. Unauthenticated → bounce to /login (with a `next` so they
 *      come back here after).
 *   2. Authenticated as non-employee (HR/admin/owner/finance) →
 *      bounce to /dashboard. The /v1/me/* API endpoints would 403
 *      anyway, but routing prevents the brief flash of an empty
 *      dashboard before the API call fails.
 *
 * Role decoded from the access token's claims rather than waiting
 * for the AuthContext to hydrate. The router runs beforeLoad
 * synchronously; relying on the context value would race the
 * one-shot silent-refresh on mount and produce a redirect flicker.
 */
export const meLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'me-layout',
  beforeLoad: ({ location }) => {
    if (!isAuthenticated()) {
      throw redirect({
        to: '/login',
        search: { next: location.pathname },
      });
    }
    const token = getAccessToken();
    const role = token ? decodeJwtRoleClaim(token) : null;
    // ADR-0025: `it` is also an employee (own /me/assets + profile), so admit it
    // alongside `employee`; other privileged roles still bounce to /dashboard.
    if (role && role !== 'employee' && role !== 'it') {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: MeLayoutComponent,
});

function MeLayoutComponent(): JSX.Element {
  return (
    <EmployeeLayout>
      <Outlet />
    </EmployeeLayout>
  );
}
